// @flow

const Q = require('q');
const _ = require('lodash');
import type { QPromise } from 'types/q';

const $ = require('jquery');
require('rgs-bootstrap');

const EventEmitter = require('events');

const assert = require('assert');

const keycode = require('keycode');

import ko from 'rgs-knockout';
import type { ObservableArray, PureComputed } from 'rgs-knockout';

const PromiseQueue = require('promise-queue');

const modalTemplates = {
	'nfd-content': require('../templates/nfd/modal-content.html'),
	'nfd-full-display': require('../templates/nfd/modal-full-display.html'),

	'body': require('../templates/modal.html'),
	'content': require('../templates/modal-content.html'),
	'dialog': require('../templates/modal-dialog.html'),
};


const dom = Q.Promise((resolve) => $(resolve));


export class ModalsStack extends EventEmitter {
	_stack: ObservableArray<Modal<any>>;
	_head: PureComputed<Modal<any>>;

	_containerPromise: QPromise<$>;

	_domModificationsQueue: PromiseQueue;

	constructor () {
		super();

		this._stack = ko.observableArray();

		this._head = ko.pureComputed(() => this._stack()[0]);

		this._containerPromise = dom.then(() => {
			return $('<div>', {
				class: 'modals-container',
			}).appendTo('body');
		});

		this._domModificationsQueue = new PromiseQueue;

		this._stack.subscribe((changes) => {
			this._stackChange(changes);
		}, this, 'arrayChange');

		(() => {
			let previousHead = undefined;

			ko.computed(() => {
				const head = this._head();

				if (previousHead === head) {
					return;
				}

				this._headChange(previousHead, head);

				previousHead = head;
			}).extend({
				rateLimit: 1,
			});
		})();

		this.on('shown', (modal) => {
			modal.emit('shown');
		});

		this.on('hidden', (modal) => {
			modal.emit('hidden');
		});

		this.on('added', (modal) => {
			modal.emit('added');
		});

		this.on('deleted', (modal) => {
			modal.emit('deleted');
		});
	}

	_getContainer (): $ | void {
		const { state, value } = this._containerPromise.inspect();

		assert(
			state === 'fulfilled',
			'Wait for `modals.ready()` promise resolution before using `modals`'
		);

		return value;
	}

	_stackChange (changes: Array<Modal<any>>) {
		_(changes)
			.filter({ status: 'added' })
			.each(({ value: modal }) => {
				this._domModificationsQueue.push(() => this._attachModal(modal)).done();
				this.emit('added', modal);
			});
		_(changes)
			.filter({ status: 'deleted' })
			.each(({ value: modal }) => {
				this._domModificationsQueue.push(() => {
					return this._hideModal(modal)
						.then(() => this._detachModal(modal));
				}).done();
				this.emit('deleted', modal);
			});
	}

	_attachModal (modal: Modal<any>) {
		const container = this._getContainer();
		if (container) {
			modal._createModal();

			container.append(modal._$modal);

			modal._applyBindings();
		}
	}

	_detachModal (modal: Modal<any>) {
		modal._$modal.detach();
	}

	_showModal (modal: ?Modal<any>) {
		if (!modal) {
			return Promise.resolve();
		}

		const promise = modal._show();

		// HACK: override bootstrap's dismiss handlers with our own
		const bsModal = modal._bsModalInstance();

		let ignoreBackdropClick = false;

		bsModal.$element
			.off('.dismiss.bs.modal')
			.on('click.dismiss.bs.modal', '[data-dismiss="modal"]', (e) => {
				this.remove(modal);
				e.stopImmediatePropagation();
			})
			.on('keydown.dismiss.bs.modal', (e) => {
				if (!bsModal.options.keyboard) {
					return;
				}
				if (e.which === keycode('escape')) {
					this.remove(modal);
				}
			})
			.on('click.dismiss.bs.modal', (e) => {
				if (ignoreBackdropClick) {
					ignoreBackdropClick = false;
					return;
				}
				if (e.target !== e.currentTarget) {
					return;
				}
				if (bsModal.options.backdrop === 'static') {
					bsModal.$element[0].focus();
				} else {
					this.remove(modal);
				}
			});

		bsModal.$dialog
			.off('.dismiss.bs.modal')
			.on('click.dismiss.bs.modal', '[data-dismiss="modal"]', (e) => {
				this.remove(modal);
				e.stopImmediatePropagation();
			})
			.on('mousedown.dismiss.bs.modal', () => {
				bsModal.$element.one('mouseup.dismiss.bs.modal', (e) => {
					if ($(e.target).is(bsModal.$element)) {
						ignoreBackdropClick = true;
					}
				});
			});

		return promise;
	}

	_hideModal (modal: ?Modal<any>) {
		if (!modal) {
			return Promise.resolve();
		}

		const promise = modal._hide();

		const bsModal = modal._bsModalInstance();

		bsModal.$element.off('.dismiss.bs.modal');
		bsModal.$dialog.off('.dismiss.bs.modal');

		return promise;
	}

	_headChange (oldModal: ?Modal<any>, newModal: ?Modal<any>) {
		if (oldModal) {
			this._domModificationsQueue.push(() => this._hideModal(oldModal)).done();
			this.emit('hidden', oldModal);
		}

		if (newModal) {
			this._domModificationsQueue.push(() => this._showModal(newModal)).done();
			this.emit('shown', newModal);
		}
	}

	ready () {
		return this._containerPromise.then(_.constant(undefined));
	}

	_unshift (modal: Modal<any>) {
		this._stack.unshift(modal);
	}

	remove (modal: ?Modal<any>) {
		if (!modal) {
			return;
		}
		this._stack.remove(modal);
	}

	unshiftOrBringUp (modal: Modal<any>) {
		this._stack.valueWillMutate();
		const stack = this._stack.peek();
		if (_.includes(stack, modal)) {
			_.pull(stack, modal);
		}
		stack.unshift(modal);
		this._stack.valueHasMutated();
	}
}

type ModalOptions<T> = {
	templateMode: 'body' | string,

	title: ?string,

	// options for ko template binding
	template: ?{
		html: any,
		data: T
	},

	// specify component to use (instead of `template`)
	component: ?any,

	// should modal be closable (by backdrop clicks, ESC key or close button)
	userCanClose: boolean,

	// css classes added to .modal-dialog
	css: {
		[string]: any
	},
}

export class Modal<T> extends EventEmitter {
	_options: ModalOptions<T>;
	_$modal: any;

	constructor (options: ModalOptions<T>) {
		super();

		if (!options.templateMode) {
			console.warn('Please specify modal `templateMode` explicitly.');
		}

		this._options = _.defaults({}, options, {
			// specifies what exactly does `template` option meen
			// * 'body' - template should be inserted in `.modal-body`
			// * 'content' - template should be inserted in `.modal-content`
			// * 'dialog' - template should be inserted in `.modal`
			templateMode: 'body',

			// title text
			// only makes sense with `templateMode: 'body'`
			title: undefined,

			// options for ko template binding
			template: undefined,

			// specify component to use (instead of `template`)
			component: undefined,

			// should modal be closable (by backdrop clicks, ESC key or close button)
			userCanClose: true,

			// css classes added to .modal-dialog
			css: {},
		});

		// HACK, TODO: Add `component` support to modal templates
		if (!this._options.template && this._options.component) {
			const { component } = this._options;
			this._options.template = {
				data: { component },
				html: '<!-- ko component: component --><!-- /ko -->',
			};
		}
	}

	_createModal () {
		this._$modal = $($.parseHTML(modalTemplates[this._options.templateMode]()));

		this._$modal.modal(this._getBootstrapModalOptions());
	}

	_getBootstrapModalOptions () {
		const { userCanClose } = this._options;
		return {
			show: false,
			backdrop: userCanClose ? true : 'static',
			keyboard: Boolean(userCanClose),
		};
	}

	_applyBindings () {
		ko.applyBindings({ options: this._options }, this._$modal[0]);
	}

	_bsModalInstance () {
		return this._$modal.data('bs.modal');
	}

	_show () {
		return Q(this._bsModalInstance().show());
	}

	_hide () {
		return Q(this._bsModalInstance().hide());
	}
}
